import { useContext, useState } from 'react';
import './TillOrderSheet.css';
import { resolveImageSrc } from '../../api/client';
import { formatDollars } from '../../shared/functions/formatter';
import { Button, Dialog, EmptyState, IconButton } from '../../ui';
import { CashierContext } from '../Cashier';
import ComputeChange from '../modals/ComputeChange';
import ConfirmRecord from '../modals/ConfirmRecord';
import { ChangeGlyph, RecordGlyph, ResetGlyph } from './tillIcons';

type TillOrderSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * The phone's Current Order (spec 29-39).
 *
 * The shared Dialog owns the modal contract (focus, Escape, backdrop, scroll
 * lock); this component recomposes the SAME cart state into the mobile hierarchy
 * - one touch line per product with a +/- stepper, a discounts line, the
 * canonical total and Record sale as the primary action. It reuses the existing
 * record and change flows rather than inventing a second order model.
 */
const TillOrderSheet = ({ open, onOpenChange }: TillOrderSheetProps) => {
  const {
    state: { items, bundles, total, savings },
    dispatch,
    completeOrder,
    canUndo,
    applyUndo,
  } = useContext(CashierContext);
  /** Which of the existing flows the sheet handed off to (spec 37/38). */
  const [panel, setPanel] = useState<'none' | 'record' | 'change'>('none');
  const countLabel = items.length === 1 ? '1 item' : items.length + ' items';
  const appliedLabel = bundles.map((bundle) => (bundle.quantity > 1 ? bundle.quantity + '× ' + bundle.title : bundle.title)).join(', ');

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        title="Current order"
        description={countLabel}
        className="tillOrderSheet"
        // Spec 39: an obvious close, on the title row instead of a row of its own;
        // Escape and the backdrop still work.
        headerAction={
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        }
      >
        <div className="tillOrderBody">
          {items.length === 0 ? (
            <EmptyState title="no items in cart" />
          ) : (
            /* Spec 31/34: vertically composed, touch-first lines - never the
               desktop table. */
            <ul className="tillOrderLines">
              {items.map(({ id, img, title, price, quantity, subtotal }) => (
                <li className="tillOrderLine" key={id}>
                  <img className="tillOrderThumb" src={resolveImageSrc(img)} alt="" />
                  <div className="tillOrderLineMain">
                    <span className="tillOrderLineName">{title}</span>
                    <span className="tillOrderLineUnit money">{formatDollars(price)} each</span>
                  </div>
                  {/* Spec 32/33: +/- controls; the line disappears when the
                      quantity reaches zero, matching the desktop REMOVE. */}
                  <div className="tillOrderStepper">
                    <IconButton
                      label={'Decrease ' + title + ' quantity'}
                      className="tillOrderStep"
                      onClick={() => dispatch({ type: 'REMOVE', payload: { id, price } as Item })}
                    >
                      <span aria-hidden="true">−</span>
                    </IconButton>
                    <span className="tillOrderQty">{quantity}</span>
                    <IconButton
                      label={'Increase ' + title + ' quantity'}
                      className="tillOrderStep"
                      onClick={() => dispatch({ type: 'ADD', payload: { id, title, price, img } })}
                    >
                      <span aria-hidden="true">+</span>
                    </IconButton>
                  </div>
                  <span className="tillOrderLineSub money">{formatDollars(subtotal)}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Spec 35: bundle savings as one plain "Discounts" line. */}
          {savings > 0 && (
            <div className="tillOrderDiscounts" title={appliedLabel}>
              <span>Discounts</span>
              <span className="money">-{formatDollars(savings)}</span>
            </div>
          )}
          {/* Spec 36: the one canonical total. */}
          {items.length > 0 && (
            <div className="tillOrderTotal">
              <span>Total</span>
              <span className="money">{formatDollars(total)}</span>
            </div>
          )}
          {canUndo && (
            <div className="cartUndo" role="status">
              <span>Order cleared.</span>
              <Button variant="secondary" size="sm" onClick={applyUndo}>
                Undo
              </Button>
            </div>
          )}
        </div>

        {/* Spec 37/38, tightened after review: the same three glyphs the desktop
            action row uses, in ONE row, so the sheet stops stacking three
            full-width buttons. Record stays the primary; the words survive as
            the accessible names. */}
        <div className="tillOrderActions">
          <Button
            variant="secondary"
            className="tillOrderAction"
            aria-label="Reset"
            onClick={() => dispatch({ type: 'RESET' })}
            disabled={items.length === 0}
          >
            <ResetGlyph />
          </Button>
          <Button
            variant="primary"
            className="tillOrderAction"
            aria-label="Record sale"
            disabled={items.length === 0}
            onClick={() => {
              onOpenChange(false);
              setPanel('record');
            }}
          >
            <RecordGlyph />
          </Button>
          <Button
            variant="secondary"
            className="tillOrderAction"
            aria-label="Change"
            onClick={() => {
              onOpenChange(false);
              setPanel('change');
            }}
          >
            <ChangeGlyph />
          </Button>
        </div>
      </Dialog>

      <Dialog open={panel === 'record'} onOpenChange={(next) => setPanel(next ? 'record' : 'none')} title="Confirm Record" size="lg">
        <div className="ui-dialog-dismiss">
          <Button variant="secondary" size="sm" onClick={() => setPanel('none')}>
            Close
          </Button>
        </div>
        <ConfirmRecord
          onRecorded={() => {
            // Same completion contract as the desktop pane: the display is told
            // the order finished before the cart clears, and the clear is
            // RECORDED so a paid order never offers undo.
            completeOrder();
            dispatch({ type: 'RECORDED' });
          }}
        />
      </Dialog>

      <Dialog open={panel === 'change'} onOpenChange={(next) => setPanel(next ? 'change' : 'none')} title="Change" size="lg">
        <div className="ui-dialog-dismiss">
          <Button variant="secondary" size="sm" onClick={() => setPanel('none')}>
            Close
          </Button>
        </div>
        <ComputeChange />
      </Dialog>
    </>
  );
};

export default TillOrderSheet;
